import { Loader2, Sparkles, Wand2, ArrowRight, PartyPopper, Briefcase, Flame } from "lucide-react";

const TONES = [
  { id: "playful", label: "Playful", icon: PartyPopper, blurb: "Witty, warm, a little cheeky." },
  { id: "serious", label: "Serious", icon: Briefcase, blurb: "Measured, precise, boardroom-ready." },
  { id: "degen", label: "Degen", icon: Flame, blurb: "gm. wagmi. Max energy, still honest." },
];

export function TonePicker({ tone, setTone, disabled, compact = false }) {
  return (
    <div data-testid="wp-tone-picker" role="radiogroup" aria-label="Whitepaper tone">
      {!compact && (
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">Pick a tone</div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {TONES.map((t) => {
          const Icon = t.icon;
          const active = tone === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => setTone(t.id)}
              data-testid={`wp-tone-${t.id}`}
              data-state={active ? "on" : "off"}
              className={`text-left border px-3 py-2.5 transition-colors disabled:opacity-50 ${
                active
                  ? "border-[#cfff04] bg-[#cfff04]/10 text-[#cfff04]"
                  : "border-[#3a3a3a] text-[#e2e2e2] hover:border-[#cfff04] hover:text-[#cfff04]"
              }`}
            >
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                <Icon size={13} /> {t.label}
              </div>
              {!compact && <div className="text-xs text-[#e2e2e2] mt-1">{t.blurb}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function StepWhitepaper({ coin, whitepaper, wpBusy, aiError, genWhitepaper, tone, setTone, next, back }) {
  return (
    <div data-testid="step-2">
      <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-3">
        Write your whitepaper.
      </h1>
      <p className="text-[#e2e2e2] mb-8">
        Pick a tone, then Claude drafts a tagline, vision, utility, tokenomics, and a 4-phase roadmap.
        You can edit on your coin page after publishing.
      </p>

      {!whitepaper ? (
        <div className="border border-[#3a3a3a] bg-[#121212] p-6 sm:p-8">
          <TonePicker tone={tone} setTone={setTone} disabled={wpBusy} />
          <div className="text-center mt-6">
            <Wand2 size={28} className="mx-auto text-[#cfff04] mb-3" />
            <p className="text-[#e2e2e2] mb-5">
              Generate a launch-ready whitepaper for {coin.name} in a <span className="text-[#cfff04]">{tone}</span> voice.
            </p>
            <button
              onClick={() => genWhitepaper()}
              disabled={wpBusy}
              data-testid="gen-whitepaper"
              className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4 inline-flex items-center gap-2"
            >
              {wpBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Generate Whitepaper
            </button>
          </div>
          {aiError && (
            <div
              data-testid="ai-error-banner-2"
              className="mt-5 border border-yellow-700/40 bg-yellow-900/10 p-4 text-sm leading-relaxed text-yellow-200 text-left"
            >
              {aiError}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="whitepaper-preview">
          <div className="border border-[#cfff04] bg-[#121212] p-6">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-2">
              Tagline
            </div>
            <div className="font-display text-2xl font-bold">
              &ldquo;{whitepaper.tagline}&rdquo;
            </div>
          </div>
          <div className="border border-[#3a3a3a] bg-[#121212] p-6">
            <div className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] mb-2">
              Vision
            </div>
            <p className="text-sm text-[#efefef] leading-relaxed">{whitepaper.vision}</p>
          </div>
          <div className="border border-[#3a3a3a] bg-[#121212] p-6">
            <div className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] mb-2">
              Utility
            </div>
            <p className="text-sm text-[#efefef] leading-relaxed">{whitepaper.utility}</p>
          </div>
          <div className="border border-[#3a3a3a] bg-[#121212] p-6">
            <div className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] mb-3">
              Tokenomics
            </div>
            <ul className="space-y-2">
              {(whitepaper.tokenomics || []).map((t) => (
                <li key={t} className="text-sm text-[#efefef] flex gap-2">
                  <span className="text-[#cfff04] font-mono">▸</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[#3a3a3a] bg-[#121212] p-4 sm:p-5">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
              Not feeling it? Rewrite in another tone
            </div>
            <TonePicker tone={tone} setTone={setTone} disabled={wpBusy} compact />
            <button
              onClick={() => genWhitepaper({ force: true })}
              disabled={wpBusy}
              data-testid="regen-whitepaper"
              className="mt-3 w-full border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] py-3 font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {wpBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Regenerate — {tone}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        <button onClick={back} className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-white" data-testid="step-back">
          ← Back
        </button>
        <div className="flex items-center gap-3">
          {!whitepaper && (
            <button
              type="button"
              onClick={next}
              data-testid="skip-whitepaper"
              className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-white px-4 py-3"
            >
              Skip →
            </button>
          )}
          <button
            onClick={next}
            data-testid="create-next"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4 flex items-center gap-2"
          >
            <ArrowRight size={16} /> Next: Publish
          </button>
        </div>
      </div>
    </div>
  );
}
